/*
MIT License

Copyright (c) 2023 react-three-map authors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
import { degreesToRadians } from "@turf/turf";
interface Coords {
  longitude: number;
  latitude: number;
  altitude?: number;
}

const earthRadius = 6371008.8;

const mercatorScaleLookup: { [key: number]: number } = {};

function getMercatorScale(lat: number): number {
  const index = Math.round(lat * 1000);
  if (mercatorScaleLookup[index] === undefined) {
    mercatorScaleLookup[index] = 1 / Math.cos(degreesToRadians(lat));
  }
  return mercatorScaleLookup[index];
}

export function averageMercatorScale(
  originLat: number,
  pointLat: number,
  steps = 10
): number {
  let totalScale = 0;
  const latStep = (pointLat - originLat) / steps;
  for (let i = 0; i <= steps; i++) {
    const lat = originLat + latStep * i;
    totalScale += getMercatorScale(lat);
  }
  return totalScale / (steps + 1);
}

export function coordsToVector3(
  point: Coords,
  origin: Coords
): [number, number, number] {
  const latitudeDiff = degreesToRadians(point.latitude - origin.latitude);
  const longitudeDiff = degreesToRadians(point.longitude - origin.longitude);
  const altitudeDiff = (point.altitude || 0) - (origin.altitude || 0);

  const x =
    longitudeDiff * earthRadius * Math.cos(degreesToRadians(origin.latitude));
  const y = altitudeDiff;

  // dynamic step size based on latitude difference. calculate the mercator unit scale at origin
  // and the scale average along the line to the point for better accuracy far from origin
  const steps = Math.ceil(Math.abs(point.latitude - origin.latitude)) * 100 + 1;
  const avgScale = averageMercatorScale(origin.latitude, point.latitude, steps);

  const z =
    ((-latitudeDiff * earthRadius) / getMercatorScale(origin.latitude)) *
    avgScale;
  return [x, y, z] as [number, number, number];
}
